import { useCallback, useEffect } from "react";
import toast from "react-hot-toast";

import { useMutation, useQuery } from "@tanstack/react-query";
import { Address } from "@ton/core";
import { useAtom, useAtomValue, useSetAtom } from "jotai";

import { useTranslation } from "@arc/i18n";
import { useNetwork, useTonWallet, useTonWallets } from "@arc/sdk";
import { isValidAddress } from "@arc/shared/utils/isValidAddress";

import { allConnectionsAtom, connectionsAtom, lastEventIdAtom, transactionsQueueAtom } from "@/atoms/tonConnect";

import {
  connectRejectResponse,
  connectWalletResponse,
  disconnectResponse,
  sendBadRequestResponse,
  sendTransactionErrorResponse,
  sendTransactionSuccessResponse,
  toTonProofItemReply,
} from "./createResponse";
import {
  getManifestResponse,
  getMaxMessages,
  signTonConnectOver,
  tonConnectProofPayload,
  toTonAddressItemReply,
} from "./helpers";
import { sendEventToBridge, subscribeTonConnect } from "./requests";
import {
  type AccountConnection,
  type ConnectItemReply,
  type ConnectRequest,
  type DAppManifest,
  type TonConnectAppRequest,
  type TonConnectParams,
  type TonConnectTransactionPayload,
} from "./types";

export interface AppConnectionProps {
  params: TonConnectParams;
  replyItems?: ConnectItemReply[];
  manifest?: DAppManifest;
}

export const useConnectionResponseMutation = () => {
  const { t } = useTranslation("wallet");
  const activeAccount = useTonWallet();
  const setConnections = useSetAtom(connectionsAtom);

  return useMutation<undefined, Error, AppConnectionProps>({
    mutationFn: async ({ params, replyItems, manifest }) => {
      if (replyItems && manifest && activeAccount) {
        const response = connectWalletResponse({
          replyItems,
          // TODO: get version from package?
          appVersion: "2.0.0",
          maxMessages: getMaxMessages(activeAccount.walletVersion),
        });

        await sendEventToBridge({
          response,
          sessionKeyPair: params.sessionKeyPair,
          clientSessionId: params.clientSessionId,
        });

        setConnections((prevConnectons) => ({
          ...prevConnectons,
          [activeAccount.address]: [
            ...(prevConnectons[activeAccount.address] || []),
            {
              clientSessionId: params.clientSessionId,
              sessionKeyPair: params.sessionKeyPair,
              manifest,
            },
          ],
        }));

        toast.success(t("TONCONNECT.SUCCESSFULLY_CONNECTED"));
      } else {
        await sendEventToBridge({
          response: connectRejectResponse(),
          sessionKeyPair: params.sessionKeyPair,
          clientSessionId: params.clientSessionId,
        });

        toast.error(t("TONCONNECT.CONNECTION_CANCELLED"));
      }

      return undefined;
    },
  });
};

export interface SendTransactionAppRequest {
  id: string | number;
  connection: AccountConnection;
  payload: TonConnectTransactionPayload;
}

export interface ResponseSendProps {
  request: SendTransactionAppRequest;
  boc?: string;
}

export const useSendResponseMutation = () =>
  useMutation<undefined, Error, ResponseSendProps>({
    mutationFn: async ({ request: { connection, id }, boc }) => {
      const numericId = Number.isNaN(Number(id)) ? id : Number(id);
      const response = boc ? sendTransactionSuccessResponse(numericId, boc) : sendTransactionErrorResponse(numericId);

      await sendEventToBridge({
        response,
        sessionKeyPair: connection.sessionKeyPair,
        clientSessionId: connection.clientSessionId,
      });

      return undefined;
    },
  });

export const useTonConnectConnectionsListener = () => {
  const { t } = useTranslation("wallet");
  const activeWallet = useTonWallet();
  const { selectWallet, list } = useTonWallets();
  const [connections, setConnections] = useAtom(connectionsAtom);
  const lastEventId = useAtomValue(lastEventIdAtom);
  const allConnections = useAtomValue(allConnectionsAtom);

  const setTonconnectTxQueue = useSetAtom(transactionsQueueAtom);

  const handleMessage = useCallback(
    async (message: TonConnectAppRequest) => {
      if (!activeWallet) return;

      if (!allConnections.length) return;

      const numericId = Number.isNaN(Number(message.request.id)) ? message.request.id : Number(message.request.id);

      if (message.request.method === "disconnect") {
        setConnections((prevConnections) => ({
          ...prevConnections,
          [activeWallet.address]:
            prevConnections?.[activeWallet.address]?.filter(
              (item) => item.clientSessionId !== message.connection.clientSessionId,
            ) ?? [],
        }));

        await sendEventToBridge({
          response: disconnectResponse(numericId),
          sessionKeyPair: message.connection.sessionKeyPair,
          clientSessionId: message.connection.clientSessionId,
        });

        return;
      }

      if (message.request.method === "sendTransaction") {
        const value = {
          connection: message.connection,
          id: message.request.id,
          payload: JSON.parse(message.request.params[0]),
        };

        const requestSessionId = message.connection.clientSessionId;
        const connectionsEntries = Object.entries(connections);
        const walletEntry = connectionsEntries.find(([_walletAddress, connectionsOfThisAddress]) =>
          connectionsOfThisAddress.find((connection) => connection.clientSessionId === requestSessionId),
        );

        if (!walletEntry) {
          toast.error(t("TONCONNECT.WALLET_NOT_FOUND"));
          return;
        }

        const requestedWalletAddress = walletEntry[0];

        if (!isValidAddress(requestedWalletAddress)) {
          toast.error(t("TONCONNECT.WALLET_ADDRESS_NOT_VALID"));
          return;
        }

        const requestedWallet = list.find((item) =>
          Address.parse(item.address).equals(Address.parse(requestedWalletAddress)),
        );

        if (!requestedWallet) {
          toast.error(t("TONCONNECT.WALLET_NOT_FOUND"));
          return;
        }

        if (requestedWallet.address !== activeWallet.address) {
          selectWallet(requestedWallet.id);
        }

        setTonconnectTxQueue((prev) => [...prev, value]);

        return;
      }

      sendEventToBridge({
        response: sendBadRequestResponse(
          numericId,
          `Method "${message.request.method}" does not supported by the wallet app`,
        ),
        sessionKeyPair: message.connection.sessionKeyPair,
        clientSessionId: message.connection.clientSessionId,
      });
    },
    [activeWallet, connections, setConnections, setTonconnectTxQueue, list, t, selectWallet, allConnections],
  );

  useEffect(() => {
    if (!activeWallet || !allConnections) return;
    return subscribeTonConnect({
      handleMessage,
      connections: allConnections,
      lastEventId,
    });
  }, [activeWallet, allConnections, handleMessage, lastEventId]);
};

export const useManifest = (params: ConnectRequest | null) => {
  const { t } = useTranslation("wallet");

  return useQuery({
    queryKey: ["dapp-manifest", params],

    queryFn: async () => {
      if (!params) return;

      try {
        const manifest = await getManifestResponse(params?.manifestUrl);

        const isValid =
          manifest &&
          typeof manifest.url === "string" &&
          typeof manifest.name === "string" &&
          typeof manifest.iconUrl === "string";

        if (!isValid) {
          throw new Error(t("TONCONNECT.MANIFEST_NOT_VALID"));
        }

        return manifest;
      } catch (e) {
        if (e instanceof Error) {
          toast.error(e.message);
        }

        throw e;
      }
    },
    enabled: params !== null,
  });
};

export const useConnectTonConnectAppMutation = () => {
  const activeWallet = useTonWallet();
  const { network } = useNetwork();

  return useMutation<
    ConnectItemReply[],
    Error,
    {
      request: ConnectRequest;
      manifest: DAppManifest;
      mnemonic: string[];
    }
  >({
    mutationFn: async ({ request, manifest, mnemonic }) => {
      if (!activeWallet) return [];

      const result = [] as ConnectItemReply[];

      // eslint-disable-next-line no-restricted-syntax
      for (const item of request.items) {
        if (item.name === "ton_addr") {
          // eslint-disable-next-line no-await-in-loop
          result.push(await toTonAddressItemReply(activeWallet, network, mnemonic));
        }

        if (item.name === "ton_proof") {
          // NOTE: if ledger or readonly, cancel flow

          const signTonConnect = signTonConnectOver(mnemonic);

          const timestamp = Math.floor(new Date().getTime() / 1000);

          const proof = tonConnectProofPayload(timestamp, manifest.url, activeWallet.address, item.payload);

          // eslint-disable-next-line no-await-in-loop
          result.push(await toTonProofItemReply({ signTonConnect, proof }));
        }
      }

      return result;
    },
  });
};
